// Plan 24b Phase B, Task 1.3 — Inventory View panel (Screen 15)
//
// Right-side panel (340px wide, full height) shown when the local player
// opens their inventory. Displays item cards stacked with a close button.
//
// Toggle behaviour:
//   - If payload.toggleWiring is present, subscribe to 'toggleInventory' events.
//   - Otherwise fall back to KeyI keydown listener.
//   - ESC always closes (named function reference for clean deregistration).

import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import type { ToggleWiring } from '@client/input/toggle-wiring';
import type { ItemTargetType } from '@shared/game/items';
import { itemIconUrl } from './item-assets';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type InventoryItemMode =
  | 'skillshot'
  | 'cone'
  | 'player-select'
  | 'self-cast'
  | 'path-direction';

export interface InventoryItemMeta {
  name: string;
  description: string;
  effectText: string;
  iconKey: string;
  targetType: ItemTargetType;
  itemMode: InventoryItemMode;
  damageText?: string;
}

export type InventoryViewPayload = BoardMountPayload & {
  toggleWiring?: ToggleWiring;
  itemMeta: Record<string, InventoryItemMeta>;
  onClose?: () => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** color.ui.panel-bg-alpha = rgba(30, 35, 50, 0.82) */
const PANEL_BG_ALPHA = 'rgba(30, 35, 50, 0.82)';

/** color.text.primary = #FFFFFF */
const WHITE = '#FFFFFF';

/** color.text.muted = #707888 */
const MUTED = '#707888';

/** color.text.secondary = #B0B8C8 */
const SECONDARY_TEXT = '#B0B8C8';

/** border-radius.lg = 12px */
const RADIUS_LG = '12px';

/** spacing.sm = 8px */
const SPACING_SM = '8px';

/** typography.size.h3 = 28px */
const H3_SIZE = '28px';

/** typography.size.h5 = 18px */
const H5_SIZE = '18px';

/** typography.size.caption = 14px */
const CAPTION_SIZE = '14px';

/** typography.family.headline */
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** typography.family.body */
const BODY_FAMILY = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif";

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createInventoryViewScreen(
  ctx: ScreenMountContext<InventoryViewPayload>,
): ScreenHandle {
  const { host, router, payload } = ctx;
  const { state, localPlayerId, toggleWiring, itemMeta } = payload;

  let disposed = false;
  let openingItemId: string | null = null;
  let actionControlsLocked = payload.isActionControlLocked?.() ?? false;
  const inventoryMode = payload.inventoryMode === 'use' ? 'use' : 'readOnly';
  const isReadOnly = inventoryMode !== 'use';
  const itemCards: HTMLButtonElement[] = [];

  function navigateBack(nextPayload: BoardMountPayload = payload): void {
    if (payload.onClose != null) {
      payload.onClose();
      return;
    }
    const target = payload.itemReturnTarget?.(nextPayload.state) ?? null;
    if (target != null) {
      target.beforeNavigate?.();
      router.navigate(target.screenId, target.payload ?? nextPayload);
      return;
    }
    router.navigate('board-hud', nextPayload);
  }

  // ── Resolve local player's item list ────────────────────────────────────────

  const items = state.players.get(localPlayerId)?.items ?? [];

  // ── Root panel ───────────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-inventory-view';
  root.setAttribute('data-screen-id', 'inventory');
  root.dataset.inventoryMode = inventoryMode;
  root.style.cssText = [
    'position: fixed',
    'right: 0',
    'top: 0',
    'height: 100%',
    'width: 340px',
    'max-width: min(460px, 92vw)',
    'min-width: min(340px, 92vw)',
    'z-index: 200',
    `background: linear-gradient(180deg, rgba(12, 24, 32, 0.92), ${PANEL_BG_ALPHA})`,
    'backdrop-filter: blur(8px)',
    'border-left: 1px solid rgba(255,255,255,0.12)',
    'box-shadow: -18px 0 36px rgba(0,0,0,0.35)',
    'display: flex',
    'flex-direction: column',
    'padding: 18px',
    'box-sizing: border-box',
    'overflow-y: auto',
  ].join('; ');

  // ── Header row (close button + title) ────────────────────────────────────────

  const headerRow = document.createElement('div');
  headerRow.className = 'inventory-header';
  headerRow.style.cssText = [
    'display: flex',
    'align-items: center',
    'justify-content: flex-start',
    'gap: 12px',
    'margin-bottom: 16px',
  ].join('; ');

  // Title (backpack glyph + "INVENTORY")
  const titleRow = document.createElement('div');
  titleRow.className = 'inventory-title';
  titleRow.style.cssText = ['display: flex', 'align-items: center', 'gap: 8px'].join('; ');

  const glyphEl = document.createElement('span');
  glyphEl.className = 'inventory-glyph';
  glyphEl.textContent = '🎒';
  glyphEl.style.cssText = 'font-size: 24px; line-height: 1;';

  const titleEl = document.createElement('span');
  titleEl.className = 'inventory-title-text';
  titleEl.textContent = 'INVENTORY';
  titleEl.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${H3_SIZE}`,
    'font-weight: 700',
    `color: ${WHITE}`,
    'letter-spacing: 0.05em',
  ].join('; ');

  titleRow.appendChild(glyphEl);
  titleRow.appendChild(titleEl);

  // Close button (left side)
  const closeBtn = document.createElement('button');
  closeBtn.className = 'inventory-close-btn';
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', 'Close inventory');
  closeBtn.textContent = 'X';
  closeBtn.style.cssText = [
    'width: 36px',
    'height: 36px',
    'min-width: 36px',
    'display: inline-flex',
    'align-items: center',
    'justify-content: center',
    `color: ${WHITE}`,
    'background: rgba(255, 255, 255, 0.12)',
    'border: 1px solid rgba(255,255,255,0.28)',
    'border-radius: 8px',
    'cursor: pointer',
    `font-family: ${HEADLINE_FAMILY}`,
    'font-size: 18px',
    'font-weight: 900',
    'line-height: 1',
    'padding: 0',
    'outline: none',
    'box-shadow: 0 4px 10px rgba(0,0,0,0.28)',
  ].join('; ');

  closeBtn.addEventListener('mouseenter', () => {
    closeBtn.style.background = 'rgba(255, 255, 255, 0.20)';
    closeBtn.style.borderColor = 'rgba(255,255,255,0.46)';
  });
  closeBtn.addEventListener('mouseleave', () => {
    closeBtn.style.background = 'rgba(255, 255, 255, 0.12)';
    closeBtn.style.borderColor = 'rgba(255,255,255,0.28)';
  });
  closeBtn.addEventListener('focus', () => {
    closeBtn.style.boxShadow = '0 0 0 3px rgba(232,160,32,0.45), 0 4px 10px rgba(0,0,0,0.28)';
  });
  closeBtn.addEventListener('blur', () => {
    closeBtn.style.boxShadow = '0 4px 10px rgba(0,0,0,0.28)';
  });
  closeBtn.addEventListener('click', () => {
    if (disposed) return;
    navigateBack();
  });

  headerRow.appendChild(closeBtn);
  headerRow.appendChild(titleRow);
  root.appendChild(headerRow);

  const itemStatus = document.createElement('div');
  itemStatus.className = 'inventory-item-status';
  itemStatus.style.cssText = [
    'display: none',
    'margin: -6px 0 12px',
    'padding: 8px 10px',
    'border-radius: 8px',
    'background: rgba(120, 24, 24, 0.88)',
    'border: 1px solid rgba(255, 180, 160, 0.62)',
    `font-family: ${BODY_FAMILY}`,
    `font-size: ${CAPTION_SIZE}`,
    'font-weight: 800',
    `color: ${WHITE}`,
    'line-height: 1.2',
  ].join('; ');
  root.appendChild(itemStatus);
  if (isReadOnly) {
    itemStatus.textContent = 'Read-only inventory. Items can be used only on your item turn.';
    itemStatus.style.display = 'block';
    itemStatus.style.background = 'rgba(20, 42, 68, 0.88)';
    itemStatus.style.borderColor = 'rgba(140, 190, 255, 0.52)';
  }

  // ── Item list ────────────────────────────────────────────────────────────────

  const itemList = document.createElement('div');
  itemList.className = 'inventory-item-list';
  itemList.style.cssText = [
    'display: flex',
    'flex-direction: column',
    `gap: ${SPACING_SM}`,
    'flex: 1',
  ].join('; ');

  if (items.length === 0) {
    // Empty state
    const emptyEl = document.createElement('div');
    emptyEl.className = 'inventory-empty';
    emptyEl.textContent = 'No items held';
    emptyEl.style.cssText = [
      'flex: 1',
      'display: flex',
      'align-items: center',
      'justify-content: center',
      `color: ${MUTED}`,
      `font-family: ${BODY_FAMILY}`,
      `font-size: ${CAPTION_SIZE}`,
    ].join('; ');
    itemList.appendChild(emptyEl);
  } else {
    // Item cards
    for (const itemId of items) {
      const meta = itemMeta[itemId];
      if (!meta) continue;

      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'inventory-item-card';
      card.setAttribute('data-item-id', itemId);
      card.style.cssText = [
        'display: flex',
        'width: 100%',
        'align-items: center',
        'gap: 14px',
        `background: linear-gradient(180deg, rgba(56, 44, 24, 0.88), rgba(13, 28, 35, 0.82))`,
        `border: 2px solid rgba(255, 205, 91, 0.28)`,
        `border-radius: ${RADIUS_LG}`,
        'box-shadow: inset 0 0 0 1px rgba(255,255,255,0.07), 0 8px 18px rgba(0,0,0,0.22)',
        'padding: 10px 12px',
        'min-height: 76px',
        'cursor: pointer',
        'user-select: none',
        'text-align: left',
        'font: inherit',
        'appearance: none',
        'transition: transform 150ms, border-color 150ms, box-shadow 150ms',
      ].join('; ');

      card.addEventListener('mouseenter', () => {
        card.style.transform = 'translateX(-2px)';
        card.style.borderColor = 'rgba(255, 205, 91, 0.82)';
        card.style.boxShadow =
          '0 0 18px rgba(232,160,32,0.45), inset 0 0 0 1px rgba(255,255,255,0.12)';
      });
      card.addEventListener('mouseleave', () => {
        card.style.transform = '';
        card.style.borderColor = 'rgba(255, 205, 91, 0.28)';
        card.style.boxShadow =
          'inset 0 0 0 1px rgba(255,255,255,0.07), 0 8px 18px rgba(0,0,0,0.22)';
      });

      // Real item icon from assets/textures/item-icons.
      const iconEl = document.createElement('div');
      iconEl.className = 'inventory-item-icon';
      iconEl.style.cssText = [
        'width: 58px',
        'height: 58px',
        'min-width: 58px',
        `border-radius: 10px`,
        `background: rgba(0, 0, 0, 0.28)`,
        'border: 2px solid rgba(255, 210, 110, 0.56)',
        'box-shadow: inset 0 0 12px rgba(255,210,110,0.18)',
        'display: flex',
        'align-items: center',
        'justify-content: center',
        'overflow: hidden',
      ].join('; ');

      const iconImg = document.createElement('img');
      iconImg.className = 'inventory-item-icon-img';
      iconImg.src = itemIconUrl(meta.iconKey);
      iconImg.alt = '';
      iconImg.draggable = false;
      iconImg.style.cssText = [
        'width: 100%',
        'height: 100%',
        'object-fit: cover',
        'display: block',
      ].join('; ');
      iconEl.appendChild(iconImg);

      // Item info (name + description)
      const infoEl = document.createElement('div');
      infoEl.className = 'inventory-item-info';
      infoEl.style.cssText = 'display: flex; flex-direction: column; gap: 2px;';
      infoEl.style.flex = '1';
      infoEl.style.minWidth = '0';

      const nameEl = document.createElement('div');
      nameEl.className = 'inventory-item-name';
      nameEl.textContent = meta.name;
      nameEl.style.cssText = [
        `font-family: ${BODY_FAMILY}`,
        `font-size: ${H5_SIZE}`,
        'font-weight: 700',
        `color: ${WHITE}`,
        'line-height: 1.05',
        'text-shadow: 0 2px 0 rgba(0,0,0,0.65)',
      ].join('; ');

      const descEl = document.createElement('div');
      descEl.className = 'inventory-item-desc';
      descEl.textContent = meta.description;
      descEl.style.cssText = [
        `font-family: ${BODY_FAMILY}`,
        `font-size: ${CAPTION_SIZE}`,
        'font-weight: 400',
        `color: ${SECONDARY_TEXT}`,
        'line-height: 1.15',
      ].join('; ');

      infoEl.appendChild(nameEl);
      infoEl.appendChild(descEl);

      card.appendChild(iconEl);
      card.appendChild(infoEl);

      const capturedItemId = itemId;
      card.addEventListener('click', () => {
        openInventoryItem(capturedItemId, card);
      });

      itemCards.push(card);
      itemList.appendChild(card);
    }
  }

  root.appendChild(itemList);

  // ── Mount to host ─────────────────────────────────────────────────────────────

  host.appendChild(root);

  // ── Toggle / keyboard close handlers ─────────────────────────────────────────

  let unsubscribeToggle: (() => void) | null = null;

  if (toggleWiring) {
    // Primary: subscribe to toggleWiring
    unsubscribeToggle = toggleWiring.subscribe((evt) => {
      if (disposed) return;
      if (evt.name === 'toggleInventory') {
        navigateBack();
      }
    });
  } else {
    // Fallback: listen for KeyI
    function handleKeyDown(e: KeyboardEvent): void {
      if (disposed) return;
      if (e.key === 'i' || e.key === 'I' || e.code === 'KeyI') {
        navigateBack();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    // Store cleanup in unsubscribeToggle slot for dispose()
    unsubscribeToggle = () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }

  // ESC always closes — named function for clean deregistration
  function handleEsc(e: KeyboardEvent): void {
    if (disposed) return;
    if (e.key === 'Escape') {
      navigateBack();
    }
  }

  document.addEventListener('keydown', handleEsc);

  const unsubscribeActionLock = payload.subscribeActionControlLock?.((locked) => {
    if (disposed || actionControlsLocked === locked) return;
    actionControlsLocked = locked;
    applyActionLockState();
  });
  applyActionLockState();

  function openInventoryItem(itemId: string, card: HTMLButtonElement): void {
    if (isReadOnly) {
      showItemStatus('Read-only inventory. Wait for your item turn to use this.');
      itemStatus.style.background = 'rgba(20, 42, 68, 0.88)';
      itemStatus.style.borderColor = 'rgba(140, 190, 255, 0.52)';
      return;
    }
    if (disposed || openingItemId != null || actionControlsLocked) return;

    const meta = itemMeta[itemId];
    if (meta == null) {
      showItemStatus('That item is missing its targeting data.');
      return;
    }

    openingItemId = itemId;
    itemStatus.style.display = 'none';
    card.disabled = true;
    card.dataset.opening = 'true';
    card.style.opacity = '0.72';
    card.style.cursor = 'wait';
    card.setAttribute('aria-busy', 'true');

    payload.tracePresentation?.({
      stage: 'inventory_item_click',
      activePlayerId: localPlayerId,
      itemId,
      counts: {
        itemCount: items.length,
      },
    });

    try {
      try {
        payload.previewItemInHand?.(itemId);
      } catch (err) {
        console.warn(`[inventory] item preview failed for ${itemId}:`, err);
      }

      router.navigate('item-targeting', {
        ...payload,
        itemId,
        itemMeta: meta,
        itemMode: meta.itemMode ?? 'self-cast',
      });
    } catch (err) {
      console.warn(`[inventory] failed to open item targeting for ${itemId}:`, err);
      openingItemId = null;
      card.disabled = actionControlsLocked;
      card.dataset.opening = 'false';
      card.style.opacity = actionControlsLocked ? '0.62' : '';
      card.style.cursor = actionControlsLocked ? 'not-allowed' : 'pointer';
      card.removeAttribute('aria-busy');
      showItemStatus('Could not open item targeting. Try again.');
    }
  }

  function applyActionLockState(): void {
    root.dataset.actionLocked = actionControlsLocked ? 'true' : 'false';
    for (const card of itemCards) {
      const isOpening = card.dataset.opening === 'true';
      const disabled = !isReadOnly && (actionControlsLocked || isOpening);
      card.disabled = disabled;
      card.style.pointerEvents = disabled ? 'none' : 'auto';
      card.style.opacity = actionControlsLocked && !isReadOnly ? '0.62' : isOpening ? '0.72' : '';
      card.style.cursor = isReadOnly
        ? 'default'
        : actionControlsLocked
          ? 'not-allowed'
          : isOpening
            ? 'wait'
            : 'pointer';
      card.toggleAttribute('data-action-locked', actionControlsLocked);
      card.toggleAttribute('data-read-only', isReadOnly);
    }
  }

  function showItemStatus(text: string): void {
    itemStatus.textContent = text;
    itemStatus.style.display = 'block';
  }

  // ── dispose ────────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Unsubscribe toggle wiring (or remove KeyI listener)
    if (unsubscribeToggle) {
      unsubscribeToggle();
      unsubscribeToggle = null;
    }

    // Remove ESC listener
    document.removeEventListener('keydown', handleEsc);
    unsubscribeActionLock?.();

    // Remove from DOM
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
